import winMagnitude, { BigWinThresholds } from "ags/utils/win-magnitude";
import wrapperImplementation from "gameiom/wrapper/wrapper-implementation";
import { SignalBinding } from "signals";
import { CloseCommand } from "slotworks/commands/comms/close-command";
import AutoplayModel from "slotworks/model/autoplay/autoplay-model";
import betModel from "slotworks/model/bets/bet-model";
import GameState from "slotworks/model/game-state";
import FreespinRecord from "slotworks/model/gameplay/records/freespin-record";
import BonusResult from "slotworks/model/gameplay/records/results/bonus-result";
import { Services } from "slotworks/services/services";
import SmartUIFlags from "slotworks/smart-ui/smart-ui-flags";
import { SmartUIFlag } from "slotworks/smart-ui/smart-ui-flags";
import SoundManager from "slotworks/sound/sound-manager";
import SuperState from "slotworks/state-machine/states/superstate";
import Contract from "slotworks/utils/contracts/contract";
import Logger from "slotworks/utils/logger";
import { parallel, sequence } from "slotworks/utils/race-utils";
import AnimateWinsMediator from "../mediators/animate-wins-mediator";
import {isGatewayResult} from "utils/is-gateway-result";

export default class AGSRollUp extends SuperState {

    public static animateWinsMediatorType = AnimateWinsMediator;

    protected rollupHasCompleted: boolean;
    protected cashWon: number;
    protected spinWon: number;
    protected animateWinsMediator: AnimateWinsMediator;

    protected collapseState: Function;

    protected signalBindings: SignalBinding[] = [];

    protected consideredWin: number;

    protected inFreespins: boolean = false;
    protected pendingFreespins: boolean = false;
    protected hasBonusWin: boolean = false;
    protected collapsing: boolean;
    protected waitingForClose: boolean;

    public onEnter() {
        this.collapsing = false;
        this.waitingForClose = false;
        this.rollupHasCompleted = false;

        SmartUIFlags.set(SmartUIFlag.AWAITING_RESPONSE, false);

        this.cashWon = GameState.getCurrentGame().getTotalWin();

        this.spinWon = GameState.getCurrentGame().getCurrentRecord().cashWon;

        this.inFreespins = GameState.getCurrentGame().getFreespinsPlayed() > 0;
        this.hasBonusWin = GameState.getCurrentGame().getCurrentRecord().getResultsOfType(BonusResult).length > 0;
        this.pendingFreespins = GameState.getCurrentGame().areFreespinsPending();

        this.consideredWin = this.getConsideredWin();

        this.signalBindings.push(betModel.onCreditSizeChanged.add(() => this.collapse()));
        this.signalBindings.push(betModel.onCreditPerLineChanged.add(() => this.collapse()));

        this.animateWinsMediator = new AGSRollUp.animateWinsMediatorType();
        this.animateWinsMediator.startAnimating(this.consideredWin).execute();

        this.executeRollup().then(() => {
            this.onRollupComplete();
        });
    }

    public onSkip() {
        SoundManager.customEvent("cancel_rollup");

        if (this.rollupHasCompleted) {
            if (AutoplayModel.isAutoPlaying() && !AutoplayModel.isFirstAutoplay()) {
                this.complete();
            } else {
                this.skip();
            }
            return;
        }

        if (!this.waitingForClose) {
            Services.transaction.setWinnings(this.cashWon).execute();
            this.animateWinsMediator.endBigWinAnimation(true).execute();

            this.closeGame().then(() => this.onRollupComplete());
        }
    }

    public onRollupComplete() {
        this.setBalance();

        this.rollupHasCompleted = true;
    }

    public onSubStateComplete() {
        if (this.rollupHasCompleted) {
            this.complete();
        } else {
            this.repeat();
        }
    }

    public onExit() {
        this.animateWinsMediator.end();
        SoundManager.customEvent("end_rollup");

        if (!this.collapsing) {
            SmartUIFlags.set(SmartUIFlag.IDLE, false);
        }

        this.setBalance();

        for (const binding of this.signalBindings) {
            binding.detach();
        }
        this.signalBindings = [];
    }

    public collapse() {
        this.collapsing = true;
        SoundManager.customEvent("end_rollup");
        this.animateWinsMediator.endBigWinAnimation(true).execute();

        Services.transaction.setTotalWin(0);

        super.collapse();
    }

    protected setBalance() {
        if (!this.inFreespins) {
            Services.transaction.setBalance(GameState.getCurrentGame().balance);
        }
    }

    protected playRollupAnimations(duration: number) {
        return new Contract((resolve) => {
            Services.transaction.setWinnings(this.cashWon, this.cashWon, duration).then(() => {
                this.animateWinsMediator.endBigWinAnimation().execute();
                resolve(null);
            });
        });
    }

    protected getRollUpAudioContract(winRatio: number, shouldPlayBonusSound: boolean, isHugeWin: boolean) {

        if (shouldPlayBonusSound) {
            SoundManager.customEvent("ringup1");
            return Contract.empty;
        }

        if (winRatio === 0) {
            return Contract.empty;
        }

        const bigWinSound = winMagnitude.getSoundAlias(winRatio);

        if (isHugeWin) {
            return () => SoundManager.customEventContract(bigWinSound);
        } else {
            SoundManager.customEvent(bigWinSound);
            return Contract.empty;
        }
    }

    protected getConsideredWin(): number {
        const currentRecord = GameState.getCurrentGame().getCurrentRecord();

        let consideredWin = this.cashWon;

        if (this.inFreespins) {
            consideredWin = this.spinWon;
        }

        if (this.hasBonusWin) {
            consideredWin = this.spinWon + GameState.getCurrentGame().getTotalChildrenWin(currentRecord);
        }

        return consideredWin;
    }

    protected executeRollup() {
        const winRatio = this.consideredWin / betModel.getTotalStake();
        const isBigWin = winRatio >= BigWinThresholds.BIG;
        const isHugeWin = winRatio >= BigWinThresholds.HUGE;
        const currentGame = GameState.getCurrentGame();
        const shouldPlayBonusSound = currentGame.isCurrentRecordType(FreespinRecord) && !isBigWin;
        const duration = winMagnitude.getSoundDuration(winRatio, shouldPlayBonusSound);

        Logger.log(`Playing roll up for win level ${winMagnitude.getWinLevel(winRatio)} (win ratio ${winRatio})`);

        const soundComplete = this.getRollUpAudioContract(winRatio, shouldPlayBonusSound, isHugeWin);

        const methods = [
            () => parallel<any>([
                () => this.playRollupAnimations(duration),
                () => soundComplete()
            ])
        ];

        methods.push(() => Contract.wrap(() => SoundManager.customEvent("end_rollup")));

        // since we are only here if we have winnings, this will only happen on a normal spin which has winnings
        methods.push(() => this.closeGame());

        return sequence<any>(methods);
    }

    protected closeGame() {
        this.waitingForClose = true;

        return new Contract<void>((resolve) => {
            if (!this.inFreespins && !this.hasBonusWin) {
                CloseCommand().execute();
            }

            if (GameState.getCurrentGame().areFreespinsPending()) {
                gameWrapper.enableFeatureMode();
            } else {
                gameWrapper.disableFeatureMode();
            }

            wrapperImplementation.onResume.addOnce(() => resolve());

            // Don't set idle SmartUIFlag if freespins have been triggered but not yet played
            if (this.inFreespins || !this.pendingFreespins) {
                SmartUIFlags.set(SmartUIFlag.IDLE, true);
            }

            if (isGatewayResult()) {
                SmartUIFlags.set(SmartUIFlag.IDLE, false);
            }

            gameWrapper.balancesUpdate(
                GameState.getCurrentGame().realBalance,
                GameState.getCurrentGame().freeBalance
            );
            gameWrapper.winningsUpdate(this.cashWon);
            gameWrapper.setIdle();
        });
    }
}
